import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Slider } from 'antd';
import { updateSearchFilters as update, clearSearchFilters as clear } from '../../../../actions';
import ClearButton from '../ClearButton';
import { t } from '../../../../helpers/t';

const SIZE_CONFIG = {
  'm²': {
    capacity_multiplier: 1.3,
    step_size: 1,
    min_size: 10,
    max_size: 500,
  },
  'sq ft': {
    capacity_multiplier: 0.13,
    step_size: 10,
    min_size: 100,
    max_size: 5000,
  },
};

const SizeSlider = () => {
  const dispatch = useDispatch();
  const {
    parameters: {
      size_gteq: minSize,
      size_lteq: maxSize,
    },
  } = useSelector((state) => state.searchFilters);

  const {
    locale: {
      area: {
        unit,
        format,
      },
    },
  } = useSelector((state) => state.searchResults);

  const [currentSliderMaxSize, setCurrentSliderMaxSize] = useState(maxSize);
  const [currentSliderMinSize, setCurrentSliderMinSize] = useState(minSize);
  const [filterCleared, setFilterCleared] = useState(false);

  const formattedCurrentSliderMinSize = format
    .replace('%n', currentSliderMinSize)
    .replace('%u', unit);

  const formattedCurrentSliderMaxSize = format
    .replace('%n', `${currentSliderMaxSize}${currentSliderMaxSize >= SIZE_CONFIG[unit || 'sq ft'].max_size ? '+' : ''}`)
    .replace('%u', unit);

  const calculatePersonsCapacity = (size) => t('directives.listings.listingFilters.formatting.people.over', {
    count: Math.round(
      SIZE_CONFIG[unit || 'sq ft'].capacity_multiplier * size,
    ),
  });

  const setCurrentSliderSizes = ([min, max]) => {
    setCurrentSliderMinSize(min);
    setCurrentSliderMaxSize(max);
  };

  useEffect(() => {
    if (filterCleared) {
      setCurrentSliderSizes([SIZE_CONFIG[unit || 'sq ft'].min_size, SIZE_CONFIG[unit || 'sq ft'].max_size]);
      setFilterCleared(false);
      return;
    }

    setCurrentSliderSizes([minSize || SIZE_CONFIG[unit || 'sq ft'].min_size, maxSize || SIZE_CONFIG[unit || 'sq ft'].max_size]);
  }, [filterCleared, minSize, maxSize, unit]);

  const resetFilter = () => {
    dispatch(clear(['size_lteq', 'size_gteq']));
    setCurrentSliderSizes([SIZE_CONFIG[unit || 'sq ft'].min_size, SIZE_CONFIG[unit || 'sq ft'].max_size]);
    setFilterCleared(true);
  };

  return (
    <div className="size-slider-container">
      <Slider
        range
        className="size-slider"
        min={SIZE_CONFIG[unit || 'sq ft'] && SIZE_CONFIG[unit || 'sq ft'].min_size}
        max={SIZE_CONFIG[unit || 'sq ft'] && SIZE_CONFIG[unit || 'sq ft'].max_size}
        value={[
          currentSliderMinSize || SIZE_CONFIG[unit || 'sq ft'].min_size,
          currentSliderMaxSize || SIZE_CONFIG[unit || 'sq ft'].max_size,
        ]}
        tooltip={{ formatter: null }}
        onChange={([min, max]) => setCurrentSliderSizes([min, max])}
        onAfterChange={([min, max]) => {
          const payload = {};

          if (min !== SIZE_CONFIG[unit || 'sq ft'].min_size) {
            payload.size_gteq = min;
          } else {
            dispatch(clear(['size_gteq']));
          }

          if (max !== SIZE_CONFIG[unit || 'sq ft'].max_size) {
            payload.size_lteq = max;
          } else {
            dispatch(clear(['size_lteq']));
          }

          if (payload.size_gteq || payload.size_lteq) {
            dispatch(update(payload));
          } else {
            resetFilter();
          }
        }}
      />
      <div className="filter-size-subtitles clearfix">
        <span className="pull-left size-subtitle">{formattedCurrentSliderMinSize}</span>
        <span className="pull-right size-subtitle">{formattedCurrentSliderMaxSize}</span>
      </div>
      <div className="filter-size-capacity-subtitles clearfix">
        <span className="pull-left capacity-subtitle">{calculatePersonsCapacity(currentSliderMinSize)}</span>
        <span className="pull-right capacity-subtitle">{calculatePersonsCapacity(currentSliderMaxSize)}</span>
      </div>
      <ClearButton onClick={resetFilter} />
    </div>
  );
};

export default SizeSlider;
